import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Languages } from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  Translations                                                              */
/* -------------------------------------------------------------------------- */

export const TRANSLATIONS = {
  en: {
    nav: {
      home: "Home",
      fleet: "Fleet & Pricing",
      tours: "Tours",
      locations: "Locations",
      reviews: "Reviews",
      waiver: "Waiver",
      contact: "Contact",
      bookNow: "Book Now",
      bookViaText: "Book via Text",
      openMenu: "Open menu",
      close: "Close",
    },
    hero: {
      badge: "5.0 ★ · 100+ Reviews · Central Florida",
      overline: "★ ROCKSTAR ATV RENTALS · HONDA-ONLY FLEET",
      typedWords: ["EXPLORE", "SHRED", "CONQUER", "OWN"],
      headlineTail: "Central Florida.",
      body: [
        "Guided ",
        "Honda Talon 1000",
        " & ",
        "Honda Rancher 420",
        " ATV tours across ",
        "Kissimmee · Ocala · Orlando",
        ". Two-hour beach loop, night rides, family-friendly, fully guided.",
      ],
      bookNow: "Book Now",
      bookViaText: "Book via Text",
      metaTour: "2H Beach Tour",
      metaNight: "Night Rides · $99",
      metaAges: "Ages 2+ Ride · 10+ Drive ATV · 18+ UTV",
    },
    fleet: {
      overline: "THE FLEET · HONDA ONLY",
      headingLine1: "Honda Talon",
      headingAmp: "&",
      headingLine2: "Rancher rentals.",
      subhead: [
        "Every unit is ",
        "Honda-built, dealer-serviced,",
        " and staged for the trail. Pick your machine — we'll bring the routes, the guide, and ice-cold water.",
      ],
      cardOverline: "FLEET · THE MACHINES",
      oneDriver: "1 Driver",
      seatsSuffix: "Seats",
      minAgePrefix: "Min Age",
      guided: "Guided",
      oneHour: "1-Hour",
      twoHour: "2-Hour",
      deposit: "Deposit",
      passenger: "Passenger",
      bookThisRide: "Book This Ride",
      paymentOverline: "PAYMENT · MADE SIMPLE",
      paymentTitle: "Cash for deposit — Zelle & Cash App also accepted.",
      textUsToBook: "Text Us to Book",
      variants: {
        "talon-4": "4-Seater Side-by-Side",
        "talon-2": "2-Seater Side-by-Side",
        "rancher-420": "Solo ATV / Four-Wheeler",
      },
      tags: {
        "FAMILY-FLAGSHIP": "FAMILY-FLAGSHIP",
        "DRIVERS-CHOICE": "DRIVERS-CHOICE",
        "AGGRO-RANGER": "AGGRO-RANGER",
      },
    },
    manifesto: {
      overline: "OUR MANIFESTO",
      headingLine1: "Why we",
      headingLine2: "ride.",
      subhead:
        "Rockstar ATV Rentals was built by Florida locals for anyone with a pulse and a free afternoon. Below are the four rules we ride by — no fluff, no upsells.",
      chapters: {
        "01": {
          title: "Real Trails. Real Machines.",
          body:
            "We don't do plastic rentals or scripted routes. Our garage is stocked with Honda Talon 1000s and Rancher 420s — the same machines that dominate Florida's off-road scene.",
        },
        "02": {
          title: "The Two-Hour Beach Ride.",
          body:
            "Book the flagship two-hour tour and explore a hidden creek-style beach — soft sand, crystal-clear spring-fed water, a rock cliff, and a rope swing from the trees straight into the water. Ask the guide for pictures — you'll want them.",
        },
        "03": {
          title: "Built for Families & Friends.",
          body:
            "Ages 10+ drive an ATV. 18+ drive a UTV. Ages 2+ ride shotgun. Everyone leaves muddy, sunburnt, and grinning. That's the whole point.",
        },
        "04": {
          title: "After Dark? We Ride Anyway.",
          body:
            "Bookings 8 PM or later unlock the Night Tour add-on — just $99 per group. Trail lights on, adrenaline up, Florida moon overhead.",
        },
      },
    },
    tourInfo: {
      overline: "GUIDED SPRING TOURS · FLORIDA",
      heading1: "Two hours. Crystal springs.",
      heading2: "One story you'll retell.",
      subhead:
        "Explore a hidden creek-style beach with soft sand and clear, spring-fed water — plus a rock cliff and a rope swing from the trees into the water. Ask for pictures!",
      cells: {
        beach: {
          label: "The Creek Beach",
          caption: "A spring-fed creek beach — sand, a rock cliff & a rope swing into the water.",
        },
        flagship: {
          big: "2H",
          label: "Flagship Spring Tour",
          body:
            "Swim a spring-fed creek beach with soft sand, a rock cliff, and a rope swing into crystal-clear water.",
        },
        night: {
          big: "$99",
          label: "Night Tour Add-On",
          body: "Any group booking 8 PM or later. Trail lights on, adrenaline up.",
        },
        ageAtv: {
          big: "10+",
          label: "Age to Drive ATV",
          body: "Honda Rancher 420. Kids graduate to real machines here.",
        },
        ageUtv: {
          big: "18+",
          label: "Age to Drive UTV",
          body: "Honda Talon 1000 — full-cage side-by-side, harness belted.",
        },
        fullSend: {
          label: "Full Send",
          caption: "Mud, sand, water crossings. Come dressed to get wrecked.",
        },
      },
    },
    testimonials: {
      overline: "5.0 · GOOGLE REVIEWS",
      heading: "Rockstars in the wild.",
      subhead:
        "What real riders say after their Kissimmee, Ocala, and Orlando trips. All reviews are verifiable — DM us and we'll send you the links.",
    },
    locations: {
      overline: "LOCATIONS · SERVING ALL OF CENTRAL FL",
      headingPrefix: "ATV Rentals in",
      subhead:
        "Wherever you are in Central Florida — we've got a trail nearby, a fresh Honda ready, and a guide who knows the routes cold.",
      cities: {
        Kissimmee: {
          tagline: "HOME BASE · CENTRAL FL",
          body:
            "Ground zero for Rockstar ATV Rentals — dozens of miles of private and public trail directly out of our Kissimmee staging lot.",
        },
        Ocala: {
          tagline: "NATIONAL FOREST COUNTRY",
          body:
            "The Ocala National Forest is a bucket-list ride. We shuttle Honda Talons to the trailhead for private tours.",
        },
        Orlando: {
          tagline: "VACATIONERS' FAVORITE",
          body:
            "Skip the theme-park lines. We pick riders up from the Orlando metro area for a real Florida adrenaline day.",
        },
      },
    },
    footer: {
      overline: "READY WHEN YOU ARE",
      heading1: "LET'S",
      heading2: "RIDE.",
      subhead:
        "Provide your full name, email, and phone. You'll receive a confirmation with everything you need before your Kissimmee, Ocala, or Orlando ride.",
      bookViaText: "Book via Text",
      signWaiver: "Sign the Waiver",
      contact: "CONTACT",
      clickToText: "Click to Text",
      explore: "EXPLORE",
      links: {
        fleet: "The Fleet",
        manifesto: "Manifesto",
        beach: "Beach Tours",
        locations: "Locations",
        reviews: "Reviews",
        waiver: "Waiver",
      },
      seo: [
        {
          title: "ATV Rentals Kissimmee, FL",
          body:
            "Rent Honda Talon 1000 UTVs and Rancher 420 ATVs directly from our Kissimmee hub. Guided beach tours, family-safe, night rides available.",
        },
        {
          title: "ATV Rentals Ocala, FL",
          body:
            "Serving Ocala National Forest riders with premium Honda side-by-side rentals and expert local guides who know every trail.",
        },
        {
          title: "ATV Rentals Orlando, FL",
          body:
            "The best off-road adventure near Orlando theme parks. Rockstar ATV Rentals delivers guided Honda tours, mud rides, and beach loops.",
        },
      ],
      copyright: "All rights reserved",
      createdBy: "Website Created by",
      privacyPolicy: "Privacy Policy",
      messagingTerms: "Messaging Terms",
    },
    booking: {
      title: "Book by Text",
      subtitle:
        "Tell us the basics and we'll open your text app pre-filled — send it to us and we reply fast.",
      fullName: "Your Name *",
      email: "Email *",
      phone: "Phone / SMS *",
      atvs: "How many ATVs (Rancher 420)?",
      sxs: "How many Side-by-Sides (Talon)?",
      seater: "If Talon: 2-seater or 4-seater?",
      seaterOptions: ["", "2-Seater", "4-Seater", "Mix"],
      people: "How many people total?",
      kids: "Kids under 10? Ages?",
      duration: "Preferred tour duration",
      durationOptions: ["", "1 Hour", "2 Hours", "Beach Tour (2H)", "Custom"],
      datetime: "Preferred date & time",
      preview: "Preview message",
      send: "Send Text",
      close: "Close",
      requiredError: "Please fill in your name, email, and phone before sending.",
      invalidEmail: "That email looks off — please double-check.",
      invalidPhone: "Please enter a valid phone number.",
    },
    floatingBook: "Book via Text",
    waiver: {
      lock: {
        title: "Access Required",
        subtitle: "Enter the access code from your booking confirmation to view the waiver.",
        placeholder: "Access code",
        submit: "Unlock Waiver",
        error: "Invalid code. Contact us if you need help.",
        contactUs: "Text us if you need the code",
      },
      overline: "RIDER RELEASE",
      title: "Rockstar ATV Rentals Waiver",
      subtitle:
        "Please read carefully. Sign electronically below. You must complete this waiver before your ride.",
      section: {
        rider: "Rider Information",
        contact: "Contact & Address",
        minors: "Minors (under 18)",
        helmet: "Helmet Policy",
        release: "Release of Liability",
        id: "Photo ID",
        signature: "Signature",
      },
      fields: {
        fullName: "Full Name",
        dob: "Date of Birth",
        phone: "Phone",
        email: "Email",
        address: "Street Address",
        city: "City",
        state: "State",
        zip: "ZIP",
        minorName: "Minor's Full Name",
        minorDob: "Minor's Date of Birth",
        minorRelation: "Relationship",
      },
      minors: {
        add: "Add another minor",
        remove: "Remove",
        consent:
          "I am the parent or legal guardian of the minor(s) listed and consent to their participation.",
      },
      helmet: {
        agree:
          "I will wear a DOT/SNELL-approved helmet at all times while operating or riding as a passenger.",
        refuse: "I am 18+ and I refuse helmet use (waiving helmet protection).",
      },
      release: {
        agree:
          "I have read and agree to the Release of Liability, Assumption of Risk, and Rockstar ATV Rentals safety terms below.",
      },
      id: {
        upload: "Upload photo of your government-issued ID",
        replace: "Replace ID photo",
        hint: "Front only. Driver's License or Passport photo page.",
      },
      signature: {
        prompt: "Sign in the box below with your finger or mouse",
        clear: "Clear signature",
      },
      submit: "Submit Waiver",
      submitting: "Submitting…",
      success: {
        title: "Waiver Signed.",
        body:
          "Thanks, {name} — your signed waiver has been recorded. You're all set to ride. Next step: lock in your date & time.",
        bookBtn: "Book Your Ride",
        homeBtn: "Back to Home",
      },
      errors: {
        required: "Please fill in this field.",
        agreeRequired: "You must agree to continue.",
        signatureRequired: "Please sign before submitting.",
        submitFailed:
          "We couldn't submit your waiver. Please try again or text us and we'll help you finish.",
      },
      autosave: "Saved locally",
    },
    lang: { switchTo: "Español", short: "ES" },
  },

  /* ------------------------------------------------------------------------- */
  es: {
    nav: {
      home: "Inicio",
      fleet: "Flota y Precios",
      tours: "Tours",
      locations: "Ubicaciones",
      reviews: "Reseñas",
      waiver: "Renuncia",
      contact: "Contacto",
      bookNow: "Reservar",
      bookViaText: "Reservar por Texto",
      openMenu: "Abrir menú",
      close: "Cerrar",
    },
    hero: {
      badge: "5.0 ★ · 100+ Reseñas · Florida Central",
      overline: "★ ROCKSTAR ATV RENTALS · SOLO HONDA",
      typedWords: ["EXPLORA", "DOMINA", "CONQUISTA", "VIVE"],
      headlineTail: "Florida Central.",
      body: [
        "Tours guiados en ",
        "Honda Talon 1000",
        " y ",
        "Honda Rancher 420",
        " ATV por ",
        "Kissimmee · Ocala · Orlando",
        ". Recorrido de playa de dos horas, tours nocturnos, para toda la familia, totalmente guiados.",
      ],
      bookNow: "Reservar",
      bookViaText: "Reservar por Texto",
      metaTour: "Tour de Playa 2H",
      metaNight: "Tours Nocturnos · $99",
      metaAges: "Pasajero 2+ · Conducir ATV 10+ · UTV 18+",
    },
    fleet: {
      overline: "LA FLOTA · SOLO HONDA",
      headingLine1: "Rentas Honda Talon",
      headingAmp: "y",
      headingLine2: "Rancher.",
      subhead: [
        "Cada unidad es ",
        "Honda, con servicio de agencia,",
        " y lista para el sendero. Elige tu máquina — nosotros ponemos rutas, guía y agua fría.",
      ],
      cardOverline: "FLOTA · LAS MÁQUINAS",
      oneDriver: "1 Conductor",
      seatsSuffix: "Asientos",
      minAgePrefix: "Edad mín.",
      guided: "Guiado",
      oneHour: "1 Hora",
      twoHour: "2 Horas",
      deposit: "Depósito",
      passenger: "Pasajero",
      bookThisRide: "Reservar Esta Aventura",
      paymentOverline: "PAGO · SIMPLE",
      paymentTitle: "Efectivo para depósito — también aceptamos Zelle y Cash App.",
      textUsToBook: "Envíanos un Texto",
      variants: {
        "talon-4": "Side-by-Side 4 Plazas",
        "talon-2": "Side-by-Side 2 Plazas",
        "rancher-420": "ATV Solo / Cuatrimoto",
      },
      tags: {
        "FAMILY-FLAGSHIP": "FAMILIAR",
        "DRIVERS-CHOICE": "PREFERIDO",
        "AGGRO-RANGER": "AGRESIVO",
      },
    },
    manifesto: {
      overline: "NUESTRO MANIFIESTO",
      headingLine1: "Por qué",
      headingLine2: "montamos.",
      subhead:
        "Rockstar ATV Rentals fue creado por locales de Florida para cualquiera con pulso y una tarde libre. Estas son las cuatro reglas por las que rodamos — sin adornos, sin ventas forzadas.",
      chapters: {
        "01": {
          title: "Senderos Reales. Máquinas Reales.",
          body:
            "No hacemos rentas de plástico ni rutas ensayadas. Nuestro garaje está cargado con Honda Talon 1000 y Rancher 420 — las mismas máquinas que dominan el off-road de Florida.",
        },
        "02": {
          title: "El Recorrido de Playa de Dos Horas.",
          body:
            "Reserva el tour estrella de dos horas y explora una playa escondida tipo arroyo — arena suave, agua cristalina de manantial, un acantilado de roca y un columpio de cuerda desde los árboles directo al agua. Pídele fotos a tu guía — las vas a querer.",
        },
        "03": {
          title: "Hecho para Familias y Amigos.",
          body:
            "10+ años conducen ATV. 18+ conducen UTV. 2+ años pueden ir de pasajero. Todos salen embarrados, quemados por el sol y sonriendo. Ese es todo el punto.",
        },
        "04": {
          title: "¿De Noche? Montamos Igual.",
          body:
            "Reservas de 8 PM en adelante desbloquean el Tour Nocturno — solo $99 por grupo. Luces de sendero encendidas, adrenalina alta, luna de Florida arriba.",
        },
      },
    },
    tourInfo: {
      overline: "TOURS GUIADOS EN MANANTIALES · FLORIDA",
      heading1: "Dos horas. Manantiales cristalinos.",
      heading2: "Una historia que contarás una y otra vez.",
      subhead:
        "Explora una playa escondida tipo arroyo con arena suave y agua transparente de manantial — además de un acantilado y un columpio de cuerda desde los árboles al agua. ¡Pídenos fotos!",
      cells: {
        beach: {
          label: "La Playa del Arroyo",
          caption:
            "Una playa de manantial — arena, un acantilado de roca y un columpio de cuerda al agua.",
        },
        flagship: {
          big: "2H",
          label: "Tour Estrella de Manantiales",
          body:
            "Nada en una playa de manantial con arena suave, un acantilado de roca y un columpio de cuerda al agua cristalina.",
        },
        night: {
          big: "$99",
          label: "Extra Tour Nocturno",
          body:
            "Cualquier grupo que reserve 8 PM o más tarde. Luces de sendero encendidas, adrenalina al máximo.",
        },
        ageAtv: {
          big: "10+",
          label: "Edad para Conducir ATV",
          body: "Honda Rancher 420. Aquí los chicos se gradúan a máquinas reales.",
        },
        ageUtv: {
          big: "18+",
          label: "Edad para Conducir UTV",
          body:
            "Honda Talon 1000 — side-by-side con jaula completa y arneses de seguridad.",
        },
        fullSend: {
          label: "A Todo Dar",
          caption:
            "Barro, arena, cruces de agua. Ven vestido para ensuciarte por completo.",
        },
      },
    },
    testimonials: {
      overline: "5.0 · RESEÑAS DE GOOGLE",
      heading: "Rockstars en acción.",
      subhead:
        "Lo que dicen riders reales después de sus tours en Kissimmee, Ocala y Orlando. Todas las reseñas son verificables — escríbenos y te mandamos los enlaces.",
    },
    locations: {
      overline: "UBICACIONES · TODA FLORIDA CENTRAL",
      headingPrefix: "Rentas de ATV en",
      subhead:
        "Donde estés en Florida Central — tenemos un sendero cerca, una Honda lista y un guía que conoce las rutas al detalle.",
      cities: {
        Kissimmee: {
          tagline: "SEDE PRINCIPAL · FL CENTRAL",
          body:
            "Punto cero para Rockstar ATV Rentals — decenas de millas de senderos privados y públicos directamente desde nuestro lote en Kissimmee.",
        },
        Ocala: {
          tagline: "TIERRA DEL BOSQUE NACIONAL",
          body:
            "El Bosque Nacional de Ocala es una ruta imperdible. Llevamos los Honda Talon al inicio del sendero para tours privados.",
        },
        Orlando: {
          tagline: "FAVORITO DE VACACIONISTAS",
          body:
            "Sáltate las filas de los parques. Recogemos riders en el área metropolitana de Orlando para un día real de adrenalina en Florida.",
        },
      },
    },
    footer: {
      overline: "LISTOS CUANDO TÚ ESTÉS",
      heading1: "A",
      heading2: "MONTAR.",
      subhead:
        "Ingresa tu nombre completo, email y teléfono. Recibirás una confirmación con todo lo necesario antes de tu recorrido en Kissimmee, Ocala u Orlando.",
      bookViaText: "Reservar por Texto",
      signWaiver: "Firmar la Renuncia",
      contact: "CONTACTO",
      clickToText: "Toca para Enviar Texto",
      explore: "EXPLORAR",
      links: {
        fleet: "La Flota",
        manifesto: "Manifiesto",
        beach: "Tours de Playa",
        locations: "Ubicaciones",
        reviews: "Reseñas",
        waiver: "Renuncia",
      },
      seo: [
        {
          title: "Rentas de ATV en Kissimmee, FL",
          body:
            "Renta Honda Talon 1000 UTVs y Rancher 420 ATVs directamente desde nuestro centro en Kissimmee. Tours de playa guiados, seguros para familias, tours nocturnos disponibles.",
        },
        {
          title: "Rentas de ATV en Ocala, FL",
          body:
            "Atendemos a riders del Bosque Nacional de Ocala con rentas premium de side-by-side Honda y guías locales expertos que conocen cada sendero.",
        },
        {
          title: "Rentas de ATV en Orlando, FL",
          body:
            "La mejor aventura off-road cerca de los parques de Orlando. Rockstar ATV Rentals ofrece tours guiados Honda, recorridos de barro y de playa.",
        },
      ],
      copyright: "Todos los derechos reservados",
      createdBy: "Sitio Web Creado por",
      privacyPolicy: "Política de Privacidad",
      messagingTerms: "Términos de Mensajería",
    },
    booking: {
      title: "Reservar por Texto",
      subtitle:
        "Cuéntanos lo básico y abriremos tu app de mensajes con el texto listo — envíalo y respondemos rápido.",
      fullName: "Tu Nombre *",
      email: "Email *",
      phone: "Teléfono / SMS *",
      atvs: "¿Cuántos ATVs (Rancher 420)?",
      sxs: "¿Cuántos Side-by-Sides (Talon)?",
      seater: "Si Talon: ¿2 plazas o 4 plazas?",
      seaterOptions: ["", "2 Plazas", "4 Plazas", "Mezcla"],
      people: "¿Cuántas personas en total?",
      kids: "¿Niños menores de 10? Edades?",
      duration: "Duración preferida del tour",
      durationOptions: ["", "1 Hora", "2 Horas", "Tour de Playa (2H)", "Personalizado"],
      datetime: "Fecha y hora preferidas",
      preview: "Vista previa del mensaje",
      send: "Enviar Texto",
      close: "Cerrar",
      requiredError: "Por favor completa nombre, email y teléfono antes de enviar.",
      invalidEmail: "El email no parece válido — revísalo por favor.",
      invalidPhone: "Por favor ingresa un número de teléfono válido.",
    },
    floatingBook: "Reservar por Texto",
    waiver: {
      lock: {
        title: "Acceso Requerido",
        subtitle:
          "Ingresa el código de acceso de tu confirmación de reserva para ver la renuncia.",
        placeholder: "Código de acceso",
        submit: "Desbloquear Renuncia",
        error: "Código inválido. Contáctanos si necesitas ayuda.",
        contactUs: "Envíanos un texto si necesitas el código",
      },
      overline: "LIBERACIÓN DEL RIDER",
      title: "Renuncia de Rockstar ATV Rentals",
      subtitle:
        "Por favor lee cuidadosamente. Firma electrónicamente abajo. Debes completar esta renuncia antes de tu recorrido.",
      section: {
        rider: "Información del Rider",
        contact: "Contacto y Dirección",
        minors: "Menores (menores de 18)",
        helmet: "Política de Casco",
        release: "Liberación de Responsabilidad",
        id: "Identificación con Foto",
        signature: "Firma",
      },
      fields: {
        fullName: "Nombre Completo",
        dob: "Fecha de Nacimiento",
        phone: "Teléfono",
        email: "Email",
        address: "Dirección",
        city: "Ciudad",
        state: "Estado",
        zip: "Código Postal",
        minorName: "Nombre Completo del Menor",
        minorDob: "Fecha de Nacimiento del Menor",
        minorRelation: "Parentesco",
      },
      minors: {
        add: "Agregar otro menor",
        remove: "Quitar",
        consent:
          "Soy el padre, madre o tutor legal del/los menor(es) listado(s) y consiento su participación.",
      },
      helmet: {
        agree:
          "Usaré un casco aprobado DOT/SNELL en todo momento mientras conduzco o voy de pasajero.",
        refuse:
          "Tengo 18+ y rechazo el uso de casco (renunciando a la protección del casco).",
      },
      release: {
        agree:
          "He leído y acepto la Liberación de Responsabilidad, Asunción de Riesgo y los términos de seguridad de Rockstar ATV Rentals a continuación.",
      },
      id: {
        upload: "Sube una foto de tu identificación oficial",
        replace: "Reemplazar foto de ID",
        hint: "Solo el frente. Licencia de conducir o página de foto del pasaporte.",
      },
      signature: {
        prompt: "Firma en el recuadro abajo con tu dedo o el mouse",
        clear: "Borrar firma",
      },
      submit: "Enviar Renuncia",
      submitting: "Enviando…",
      success: {
        title: "Renuncia Firmada.",
        body:
          "Gracias, {name} — tu renuncia firmada ha sido registrada. Estás listo para montar. Siguiente paso: confirma tu fecha y hora.",
        bookBtn: "Reserva Tu Aventura",
        homeBtn: "Volver al Inicio",
      },
      errors: {
        required: "Por favor completa este campo.",
        agreeRequired: "Debes aceptar para continuar.",
        signatureRequired: "Por favor firma antes de enviar.",
        submitFailed:
          "No pudimos enviar tu renuncia. Inténtalo de nuevo o mándanos un texto y te ayudamos.",
      },
      autosave: "Guardado localmente",
    },
    lang: { switchTo: "English", short: "EN" },
  },
};

/* -------------------------------------------------------------------------- */
/*  Context + hook                                                            */
/* -------------------------------------------------------------------------- */

const LangContext = createContext({ lang: "en", t: (k) => k, setLang: () => {} });
const STORAGE_KEY = "rar_lang";

function resolve(obj, path) {
  return path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "es" ? "es" : "en";
  });

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {
      /* storage unavailable */
    }
  }, []);

  const t = useCallback(
    (path, fallback) => {
      const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
      const val = resolve(dict, path);
      if (val !== undefined) return val;
      const enVal = resolve(TRANSLATIONS.en, path);
      if (enVal !== undefined) return enVal;
      return fallback !== undefined ? fallback : path;
    },
    [lang]
  );

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>
  );
}

export function useT() {
  return useContext(LangContext);
}

/* -------------------------------------------------------------------------- */
/*  Toggle component                                                          */
/* -------------------------------------------------------------------------- */

export function LanguageToggle({ className = "" }) {
  const { lang, setLang } = useT();
  const next = lang === "en" ? "es" : "en";
  const label = next === "es" ? "ES" : "EN";
  return (
    <button
      onClick={() => setLang(next)}
      className={`inline-flex items-center gap-1.5 border border-white/25 text-white/85 hover:bg-white hover:text-black font-bold uppercase tracking-[0.18em] text-[10px] px-2.5 py-2 transition-colors ${className}`}
      aria-label={`Switch language to ${label}`}
      data-testid="lang-toggle"
      title={`Switch to ${label === "ES" ? "Español" : "English"}`}
    >
      <Languages size={12} /> {label}
    </button>
  );
}
